/* ============================================================
   TOYDROP, td-unbox.js
   The unboxing hero: a drop-in entrance on load, then a pinned
   GSAP ScrollTrigger scrub that lifts the lid and bursts the toys.

   Load contract (see sections/td-hero.liquid):
   - The MARKUP is the settled end state (open box, lid at rest,
     toys scattered). No JS, reduced motion, or any failure below
     leaves that showing.
   - An inline snippet adds `unbox-armed` to <html> pre-paint unless
     the reader prefers reduced motion. CSS keyed on that class
     hides the end-state pieces with opacity/visibility ONLY (never
     transforms, so this file can read the settled geometry back).
   - This file then injects GSAP + ScrollTrigger (local assets) and
     builds the timeline. Reduced-motion readers never fetch them.
     Any failure disarms and restores the still.

   THERE IS NO CLOSED-BOX DRAWING, AND NOTHING SWAPS. The sealed
   parcel is three sprites composited live:

        open-back    the whole open box            z 2
        lid-seated   cut from the closed drawing   z 6
        open-front   front walls, coral, logo      z 5

   with the toys and confetti at z 4, between the walls. The lid is IN FRONT of
   the whole box for the entire sequence: seated it overlaps the top of the
   front wall by its whole skirt, and at rest a lower depth clipped a quarter of
   it away. One depth means no flip to get wrong. All of it is
   identity placement from one shared 1024 canvas, so the base cannot
   move, and the box "opens" purely because the lid lifts off and
   uncovers the interior that was underneath the whole time. That is
   what makes the lid's flight continuous: it starts from the exact
   pixels it was drawn as while seated. The previous build handed off
   from that seated lid to a separately drawn tilted lid at a different
   orientation and scale, which no easing can rescue: it read as a
   teleport.

   Two independent pieces of motion:

   1. ENTRANCE (load, no scroll needed). The parcel falls from above the
      viewport on an accelerating ease and bounces TWICE with decaying
      amplitude, squashing on each impact and stretching on each rise,
      with a one-directional wobble that damps out and a ground shadow
      that tracks every contact. Under 1.6s. The copy column never waits
      for any of it. The entrance owns `.unbox-drop`; the scrub owns the
      nested `.unbox-parcel`, and they may never share an element.

   2. SCRUB (progress 0 to 1 across the pin):
        0.00-0.20  the sealed parcel breathes, highlight sweep wipes in
        0.20-0.30  anticipation squash, rebounding to exactly scale 1
        0.30-0.57  the lid lifts off, tips back, arcs up and RIGHT and
                   settles against the box's top-right corner
        0.30-0.55  CTA pulses once as the seal breaks
        0.42-0.82  the burst, which may not begin until the lid is off the
                   mouth: every toy and every confetti piece spawns
                   at the mouth INSIDE the box, hidden by the front
                   wall, and climbs out through the rim on one shared
                   path (y leads, x lags, scale grows through the rim)
        0.70-1.00  everything settles into the resting arc

   Everything animates transform/opacity only.
   ============================================================ */
(function () {
  'use strict';

  var doc = document.documentElement;
  var section = document.querySelector('[data-unbox]');
  if (!section) return;

  function startIdle() {
    section.classList.add('unbox-idle');
  }

  function disarm() {
    /* The default rendered state is the sealed PARCEL now, so simply dropping
       `unbox-armed` would leave a closed box that never opens. Settling is what
       restores the finished composite. */
    doc.classList.remove('unbox-armed');
    doc.classList.add('unbox-settled');
    startIdle();
  }

  if (!doc.classList.contains('unbox-armed')) {
    /* Reduced motion: the settled composite stands, and stays still. */
    return;
  }

  var booted = false;
  /* 2s pre-empted a slow-but-successful load: on a throttled cold start GSAP
     arrived at ~4s, so the failsafe had already settled the scene and boot then
     rewound it back to a sealed box in front of the reader. The blank-stage risk
     it was guarding against is handled in CSS instead (the parcel reveals itself
     at 1200ms), so this can go back to a length that only fires on real
     failure. Once it has fired, boot must not undo it. */
  var gaveUp = false;
  var failsafe = setTimeout(function () {
    if (!booted) {
      gaveUp = true;
      disarm();
    }
  }, 4000);

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  var urls;
  try {
    urls = JSON.parse(section.getAttribute('data-unbox-assets'));
  } catch (e) {
    urls = null;
  }
  if (!urls || !urls.gsap || !urls.st) {
    clearTimeout(failsafe);
    disarm();
    return;
  }

  function start() {
    loadScript(urls.gsap)
      .then(function () {
        return loadScript(urls.st);
      })
      .then(boot)
      .catch(function () {
        clearTimeout(failsafe);
        disarm();
      });
  }

  /* Fetch immediately rather than on an idle callback. The idle window cost
     ~350ms, during which the closed box was already painted and standing still,
     so the fall that followed read as the box teleporting upwards. Reduced
     motion still never gets here, so those readers still fetch nothing, and the
     hero sprites are already eager/high-priority so they win the race anyway. */
  start();

  /* ---------------------------------------------------------------- */

  function boot() {
    if (gaveUp) {
      /* the failsafe already showed the finished still; snapping it back to a
         sealed parcel now would be worse than never animating */
      return;
    }
    if (!window.gsap || !window.ScrollTrigger) {
      clearTimeout(failsafe);
      disarm();
      return;
    }
    booted = true;
    clearTimeout(failsafe);

    var gsap = window.gsap;
    gsap.registerPlugin(window.ScrollTrigger);
    /* Mobile browsers fire a resize every time the URL bar slides, and the
       default response is a full ScrollTrigger refresh mid-gesture, which is
       exactly the stutter people feel on phones. */
    window.ScrollTrigger.config({ ignoreMobileResize: true });

    var mobile = window.matchMedia('(max-width: 900px)').matches;
    var q = function (sel) {
      return section.querySelector(sel);
    };

    var pin = q('[data-unbox-pin]');
    var stage = q('[data-unbox-stage]');
    var open = q('[data-unbox-open]');
    var lid = q('[data-unbox-lid]');
    var openFront = q('[data-unbox-open-front]');
    var drop = q('[data-unbox-drop]');
    var parcelEl = q('[data-unbox-parcel]');
    var ground = q('[data-unbox-ground]');
    var toys = Array.prototype.slice.call(section.querySelectorAll('[data-unbox-toy]'));
    var confettiHost = q('[data-unbox-confetti]');
    var copy = q('[data-unbox-copy]');
    var cta = q('[data-unbox-cta]');
    var chip = q('[data-unbox-chip]');
    var sub = q('[data-unbox-sub]');
    var trust = q('[data-unbox-trust]');
    var hl = q('[data-unbox-hl]');
    var far = q('[data-unbox-far]');
    var mid = q('[data-unbox-mid]');
    var wash = q('[data-unbox-wash]');

    if (!pin || !stage || !open || !openFront || !lid || !toys.length) {
      disarm();
      return;
    }

    /* ---- THE BOX STANDS IN THE PHOTOGRAPH -------------------------------
       The stage is anchored to a point in the PICTURE, not to the viewport.
       object-fit: cover crops a different slice of the image at every aspect
       ratio, so a percentage of the viewport would slide the box off the
       patch of floor it is supposed to be standing on: at 1920x900 the crop
       keeps 84% of the image height, at 1024x660 it keeps only 82% of the
       width, and the clear floor moves hundreds of pixels between them.

       UNBOX_ANCHOR IS READ OFF THE APPROVED MOCKUP, which is convenient in a
       way worth stating: mockup-target-1.png is 2752x1536, exactly the source
       photograph's frame, so a fraction of the mockup IS a fraction of the
       image and the two coordinate spaces are the same one.

       Measured there, the box's body spans x 0.686..0.882 and its base sits at
       y 0.935. The shipped anchor then moved up and right of that by the
       merchant's eye on the real page (0.665 / 0.442 against the mockup's
       0.638 / 0.463), which puts the box further off the child and leaves more
       floor under it; the WIDTH is still the mockup's. The sprites occupy x 16.484%..83.672% and y 29.316%..90.25% of
       this stage (those percentages are the drawing: the PNGs are opaque edge
       to edge), so a body 0.196 wide with its base at 0.935 means a stage
       0.292 wide whose top is at 0.463. That is where these three numbers come
       from; do not round them without re-measuring the mockup.

       The box now stands ON the spot where a toy dog lies in the photograph,
       which is deliberate and is what the mockup shows: at this size the box
       is nearer the camera than the dog and covers it.

       FACE_KEEPOUT is the child's head and hair, measured the same way: a
       sprite landing on her face is the one placement that reads as a mistake
       rather than as a burst. */
    /* top 0.263, up from 0.442, by the merchant's mark on the real page: the
       lid's top edge sits at 41.5% of the frame. Higher on a real floor would
       mean further back in the room and so smaller; it can stay this size
       because the box now stands on the paper fade, not on floorboards. */
    var UNBOX_ANCHOR = { left: 0.665, top: 0.263, width: 0.292 };
    var FACE_KEEPOUT = { left: 0.185, top: 0.565, right: 0.355, bottom: 0.85 };
    /* HER RAISED LEG, measured off the same photograph: the knee's top edge is
       at y 0.545 and the leg runs from the thigh at x 0.48 out to the ankle at
       0.66. It joins the face as a keep-out because the burst now covers far
       more of the frame than it used to, and a squishy resting on a child's
       knee reads as a compositing mistake in a way one crossing plain wall
       does not. The zone is shallow on purpose: everything above y 0.54 in
       that column is wall and stays available to the arc, and the right edge
       is 0.625 rather than the ankle's 0.66 because a rectangle drawn to the
       foot also covers the patch of wall beside her knee where the mockup
       floats one of its resting sprites. */
    var LEG_KEEPOUT = { left: 0.480, top: 0.540, right: 0.625, bottom: 0.780 };
    /* PHONES SHOW THE DESKTOP SCENE, scaled: the same wide photograph as a
       band above the copy, with the parcel stood on the same floor by the
       same anchor maths. Only the parcel's size differs. At the desktop
       fraction (0.292) a phone band makes the box ~95px and the toys under
       20px, too small to read as toys, so the phone stage is a little
       larger and sits a little further left; its base stays on the floor at
       y 0.94 (top = 0.94 - 0.9025 * width * 2752/1536). The clamp in
       placeStage slides it back inside whatever the band crops. */
    var UNBOX_ANCHOR_PHONE = { left: 0.60, top: 0.407, width: 0.33 };
    /* The dinosaur in her arms and her chest, phones only. On desktop the
       burst clears it by construction; in the tighter phone band a toy could
       settle on the plush she is hugging, which reads as a compositing
       mistake rather than as part of the burst. */
    var HUG_KEEPOUT_PHONE = { left: 0.30, top: 0.55, right: 0.48, bottom: 0.80 };

    /* THERE ARE NO TOYS AT REST, and that is a decision, not an omission.
       Four sprites used to float above the box before any scroll, to fill the
       wall. With a CLOSED parcel underneath them they read as toys that had
       already spilled out of it, which gives away the reveal the scroll is
       built to deliver: nothing should be outside a sealed box. The frame is
       carried instead by the parcel itself, which is now half again the size
       it was and sits higher and further right. */
    var heroImg = document.querySelector('.td-hero__img');
    var stageCol = stage.parentNode;
    /* LIVE, not the `mobile` boolean captured at init. placeStage runs again on
       every resize, and a window dragged across 900px has to switch layouts:
       the stage is anchored into the photograph above that width and sits in
       the flow on the cream below it. Reading the frozen boolean would leave a
       resized window with the wrong one. */
    var mobileMq = window.matchMedia('(max-width: 900px)');

    /* maps a normalised point in the SOURCE image to viewport coordinates */
    function photoMap() {
      if (!heroImg || !heroImg.naturalWidth || !heroImg.naturalHeight) return null;
      var r = heroImg.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      var sc = Math.max(r.width / heroImg.naturalWidth, r.height / heroImg.naturalHeight);
      var dw = heroImg.naturalWidth * sc;
      var dh = heroImg.naturalHeight * sc;
      var op = (getComputedStyle(heroImg).objectPosition || '50% 50%').split(/\s+/);
      var px = parseFloat(op[0]);
      var py = parseFloat(op.length > 1 ? op[1] : op[0]);
      if (isNaN(px)) px = 50;
      if (isNaN(py)) py = 50;
      return {
        ox: r.left + (r.width - dw) * (px / 100),
        oy: r.top + (r.height - dh) * (py / 100),
        dw: dw,
        dh: dh
      };
    }

    function photoRect(n) {
      var m = photoMap();
      if (!m) return null;
      return {
        left: m.ox + n.left * m.dw,
        top: m.oy + n.top * m.dh,
        right: m.ox + (n.right !== undefined ? n.right : n.left + n.width) * m.dw,
        bottom: m.oy + (n.bottom !== undefined ? n.bottom : n.top) * m.dh
      };
    }

    /* Writes the stage's box. Phones skip it: the 4:5 crop is centred on the
       child and does not contain the clear floor, so there the scene sits in
       the flow below the copy on the cream field instead (see toydrop.css). */
    function placeStage() {
      if (!stageCol) return;
      var m = photoMap();
      if (!m) return;
      if (mobileMq.matches) {
        placeStagePhone(m);
        return;
      }
      /* undo fitScenePhone if the viewport crossed the breakpoint */
      var sceneMedia = section.querySelector('.td-hero__media');
      if (sceneMedia) sceneMedia.style.marginTop = '';
      var firstCopy = section.querySelector('.hero-copy > :first-child');
      if (firstCopy) firstCopy.style.marginTop = '';
      stageCol.style.marginTop = '';
      stageCol.style.marginBottom = '';
      var A = mobileMq.matches ? UNBOX_ANCHOR_PHONE : UNBOX_ANCHOR;
      var host = stageCol.offsetParent || pin;
      var hr = host.getBoundingClientRect();
      var w = A.width * m.dw;
      var left = m.ox + A.left * m.dw - hr.left;
      var top = m.oy + A.top * m.dh - hr.top;

      /* THE CLAMP MEASURES THE DRAWING, NOT THE STAGE. The stage is a square
         and the parcel does not fill it: the sprites stop at 90.25% of its
         height, and below that is the contact shadow's band and then nothing.
         Clamping on the square's own bottom therefore lifted the box for the
         sake of empty space, which at this size costs real altitude. INK_BOTTOM
         is how far down the square anything is actually painted.

         THE ANCHOR IS A POINT IN THE PICTURE, AND THE PICTURE IS CROPPED.
         object-fit: cover throws away whatever does not fit the viewport's
         aspect ratio, and the part it throws away is the part the anchor
         lives in: a tall viewport eats the right edge, a wide one eats the
         bottom. At 1024x700 that cut 60px off the box's right side and at
         1920x900 it cut 52px off its feet, both silently, because the hero
         clips rather than scrolls.

         So the box is clamped into what is actually on screen. SLIDE FIRST,
         SHRINK SECOND: the clear floor runs from source x 0.79 to the right
         edge and the box only occupies the middle 67% of this stage, so there
         is roughly 4% of the picture's width to give before its left side
         would touch the toy dog lying at x 0.785. Past that it has to get
         smaller, which is much less noticeable than a box with one wall
         missing. Sliding UP is free in the same way: the floor is continuous,
         so a box a little further back in the room still reads as standing on
         it. EDGE_PAD keeps a sliver of floor outside the box so it never
         looks welded to the frame. */
      var EDGE_PAD = 6;
      /* 0.93 of the stage, and the 0.07 left over is deliberate. The sprites
         stop at 90.25%; the contact ellipse reaches about 93.7%; the ambient
         shadow's outer third runs on to roughly 100% at three hundredths of an
         alpha. Clamping on that invisible tail cost the box 2.3% of the
         picture's height at 1920x900, which is real altitude for a shadow
         nobody can see. So the tail is allowed to meet the fold. */
      var INK_BOTTOM = 0.93;
      /* The box may slide left as far as the child's feet, which are the
         nearest photographed thing it must not sit on: her heel is at source
         x 0.545, and the box's left wall is 16.5% into the stage, so a stage
         starting at 0.50 puts the wall at 0.548. The dog it now covers is not
         a constraint any more; the mockup covers it too. */
      var MIN_LEFT = m.ox + 0.50 * m.dw - hr.left;
      if (left + w > hr.width - EDGE_PAD) {
        left = Math.max(MIN_LEFT, hr.width - EDGE_PAD - w);
        if (left + w > hr.width - EDGE_PAD) w = hr.width - EDGE_PAD - left;
      }
      /* THE HOST IS TALLER THAN WHAT IS ON SCREEN. The pin is 100svh, but the
         hero is only pulled up under the NAV, so at scroll 0 it starts below
         the yellow ticker and its bottom 38px sit past the fold. Clamping
         against hr.height therefore put the box's feet and its whole contact
         shadow exactly at the bottom edge of the first paint, which is the
         one frame that has to be right. Measuring the visible height instead
         costs the box a little altitude at rest and nothing else. */
      var visible = Math.min(hr.height, window.innerHeight - Math.max(0, hr.top));
      if (top + w * INK_BOTTOM > visible - EDGE_PAD) {
        top = Math.max(0, visible - EDGE_PAD - w * INK_BOTTOM);
        if (top + w * INK_BOTTOM > visible - EDGE_PAD) {
          w = (visible - EDGE_PAD - top) / INK_BOTTOM;
        }
      }
      if (w < 120) w = 120;

      stageCol.style.width = w + 'px';
      stageCol.style.left = left + 'px';
      stageCol.style.top = top + 'px';
    }

    /* PHONES: the parcel is a flex item of the hero (toydrop.css 27g), in
       the merchant's order: copy, parcel, button, child. The stylesheet
       reserves its square between the rating chip and the button, so there is
       nothing to compute here; clear anything a desktop-width pass wrote (a
       rotation or resize across the breakpoint) and mark it placed, which is
       what lets the stylesheet show it. */
    function placeStagePhone() {
      stageCol.style.width = '';
      stageCol.style.left = '';
      stageCol.style.top = '';
      stageCol.setAttribute('data-placed', '');
      fitScenePhone();
    }

    /* PHONES: the child's photograph runs from just under the button to the
       bottom of the hero, so it meets the next section edge to edge whatever
       the phone's height. That makes its height, and so where the child
       lands, depend on the screen; this pulls the photo up behind the button
       by exactly enough that her knees (0.45 of the drawn photo, which cover
       draws at full height on any frame taller than 1.8:1) sit KNEE_GAP px
       below the button. Closed form: with the photo ending at the pin's
       bottom, top + 0.45 * (pinBottom - top) = buttonBottom + gap.
       CAPPED at 1.3x the photo's natural 13:10 height: past that, cover
       narrows the frame into the child (at 2x on a tall phone it showed 37%
       of the width). A capped photo stands on the hero's bottom edge and the
       height left above it is wall, which the photo's masked top already
       dissolves into, so a tall phone gets a larger gap above her, never a
       strip below. The stylesheet's fixed pull-up (27g) is the no-JS
       fallback. */
    function fitScenePhone() {
      var media = section.querySelector('.td-hero__media');
      var row = section.querySelector('.hero-cta-row');
      var btn = row && (row.querySelector('.btn') || row);
      if (!media || !btn) return;
      var KNEE = 0.45;
      var KNEE_GAP = 36;
      var CAP = 1.3;
      var first = section.querySelector('.hero-copy > :first-child');
      if (first) first.style.marginTop = '';
      stageCol.style.marginTop = '';
      stageCol.style.marginBottom = '';
      var pinBottom = pin.getBoundingClientRect().bottom;
      var btnBottom = btn.getBoundingClientRect().bottom;
      var capH = CAP * media.getBoundingClientRect().width * 10 / 13;
      var top = (btnBottom + KNEE_GAP - KNEE * pinBottom) / (1 - KNEE);
      if (top < pinBottom - capH) {
        /* capped (a tall phone): the height the photo cannot take would
           all sit as one band of wall above the child. Share it four ways
           instead: above the headline, above and below the parcel, and
           above the child. */
        var share = ((pinBottom - (1 - KNEE) * capH) - (btnBottom + KNEE_GAP)) / 4;
        var stageTop = parseFloat(getComputedStyle(stageCol).marginTop) || 0;
        if (first) first.style.marginTop = share + 'px';
        stageCol.style.marginTop = stageTop + share + 'px';
        stageCol.style.marginBottom = share + 'px';
        top = pinBottom - capH;
      }
      media.style.marginTop = top - row.getBoundingClientRect().bottom + 'px';
    }

    placeStage();
    if (heroImg && !heroImg.complete) heroImg.addEventListener('load', placeStage, { once: true });
    /* The anchor is derived from the RENDERED photograph, so a width change
       moves it. ScrollTrigger.config({ignoreMobileResize:true}) already
       swallows an address-bar collapse, which is the resize we do not want to
       react to; this one has to fire, or the box drifts off its floor. */
    window.addEventListener('resize', placeStage);

    var pinVh = parseInt(pin.getAttribute(mobile ? 'data-pin-mobile' : 'data-pin-desktop'), 10);
    if (!pinVh || pinVh < 60) pinVh = mobile ? 100 : 120;
    /* A phone scene is one screen and a thumb covers a screen in one swipe;
       much more than three quarters of one read as the page being stuck. */
    if (mobile) pinVh = Math.min(pinVh, 75);

    var liveToys = toys.filter(function (t) {
      return getComputedStyle(t).display !== 'none';
    });

    /* ---- rewind to scene start, NOW ------------------------------------- */
    /* This runs the instant GSAP lands, before anything is measured. It used to
       sit behind the font/decode wait that only the solver needs, so for ~450ms
       the reader saw the box standing at rest and then teleport above the
       viewport to fall. Opacity state first, geometry later.

       The three box layers stay VISIBLE: together they are the sealed parcel.
       Only the lid has to move, and only the toys have to disappear.

       Order matters and this is why it is one synchronous block. Removing
       `unbox-armed` restores the settled CSS, which is where the lid's resting
       pose is declared. Read it, THEN park the lid at identity. No frame is
       rendered between the three, so nothing flashes. */
    gsap.set(liveToys, { autoAlpha: 0 });
    if (trust) gsap.set(trust, { autoAlpha: 0, y: 10 });
    if (wash) gsap.set(wash, { opacity: 0 });
    /* Read the resting pose out of the SETTLED css, then leave it. One
       synchronous block, so no frame is rendered in the settled state. */
    doc.classList.add('unbox-settled');
    var lidRest = {
      x: Number(gsap.getProperty(lid, 'x')) || 0,
      y: Number(gsap.getProperty(lid, 'y')) || 0,
      rot: Number(gsap.getProperty(lid, 'rotation')) || 0
    };
    /* SEATED. Identity is where the sprite was drawn on the closed parcel, so
       the composite right now is pixel-for-pixel the sealed box and the flight
       below starts from these exact pixels. No jump is possible.
       Its depth (z 6, in front of the whole box) is declared once in CSS and
       never changes: seated it overlaps the top of the front wall by its whole
       skirt, and at rest a lower depth clipped a quarter of it away. */
    doc.classList.remove('unbox-settled');
    gsap.set(lid, { x: 0, y: 0, rotation: 0, autoAlpha: 1 });
    /* the parcel exists again and owns its own opacity from here; the CSS
       reveal keyframe would otherwise keep overriding the inline value */
    if (drop) {
      drop.style.animation = 'none';
      gsap.set(drop, { autoAlpha: 1 });
    }
    doc.classList.remove('unbox-armed');

    placeStage();
    var stageRect = stage.getBoundingClientRect();

    /* ---- confetti -------------------------------------------------------- */
    var PALETTE = ['#FF5E5E', '#FFC93C', '#FF8FC7', '#4CB9FF'];
    var SHAPES = ['circle', 'square', 'strip', 'big'];
    var confetti = [];
    /* 28, down from 44. At the old box size the field framed the toys; at
       this one it filled the whole upper right of the photograph and the
       toys' arc disappeared inside it. The burst has to read as seven things
       flying, with confetti as texture around them, not as forty. */
    var count = mobile ? 22 : 28;
    for (var ci = 0; ci < count; ci++) {
      var bit = document.createElement('span');
      bit.className = 'unbox-bit unbox-bit--' + SHAPES[ci % SHAPES.length];
      bit.style.background = PALETTE[ci % PALETTE.length];
      confettiHost.appendChild(bit);
      confetti.push(bit);
    }
    /* Hidden until the solver has parked them inside the box. Every piece
       belongs to the burst now: the old landing puff spawned four of them
       outside the parcel, which contradicts confetti only ever coming from
       inside it, and it fought the burst for the same elements. */
    gsap.set(confetti, { autoAlpha: 0 });

    /* ================= 1. ENTRANCE ======================================= */
    /* Runs on load and owes nothing to the scroll timeline, or to the fonts:
       the fall is a relative translate, so a late reflow under it is harmless.
       The faller is the whole parcel wrapper, so box, lid and (invisible) toys
       land as one object. */
    var dropFrom = -(stageRect.top + stageRect.height * 0.75 + 80);
    var landed = false;

    function afterLanding() {
      if (landed) return;
      landed = true;
      /* NOT startIdle(): the settled idle loops animate the very properties
         GSAP drives during the burst (toy inner rotation, shadow scale and
         opacity), and a CSS animation beats an inline style, so leaving them
         on through the scrub made the toys twitch and the shadows flicker.
         Idle belongs to the END of the sequence; the closed box gets its own
         gentle float instead. */
      section.classList.add('unbox-landed');
    }

    /* `.unbox-drop` is a permanently absolute, stage-sized box (see the CSS).
       It used to be toggled into place with an `is-dropping` class that no rule
       ever matched, which left it inline and un-transformable, so the browser
       discarded every frame of the fall. */
    var faller = drop || open;

    /* A fall only works if the box has not already been standing there. While
       `unbox-armed` is on, the parcel is not drawn at all, so there is nothing
       on screen to teleport away from and this window can be generous: measured
       at 1180ms between arming and GSAP on a 4x-throttled 400KB/s cold load,
       against the 260ms this used to allow, which is why the fall was being
       skipped on every first visit.

       The deadline is the CSS reveal keyframe's, not a guess: inside it the
       parcel has provably never been painted, past it the reader is already
       looking at the box and it stays planted for the landing squash alone.
       Keep this in step with `unbox-await` in toydrop.css. */
    var armedAt = Number(window.__unboxArmedAt) || 0;
    var canFall = armedAt > 0 && performance.now() - armedAt < 1200;
    var fallDur = canFall ? 0.5 : 0.0001;

    var entrance = gsap.timeline({
      delay: canFall ? 0.1 : 0,
      onComplete: afterLanding
    });

    /* TWO bounces, not three, and each one longer than before.
       Three hops decaying 80/36/15px inside a second meant the last two were a
       few frames each: fast, small, opposite-signed moves read as a jitter
       rather than as weight. A heavy parcel gives one clear bounce and a small
       second one, so that is what this does. The wobble is halved for the same
       reason, and the second hop no longer counter-rotates: alternating a
       +-2deg tilt over ~6 frames is exactly what looked unreal. */
    var HOPS = [
      { rise: 0.135, dur: 0.30, squashY: 0.85, squashX: 1.12, wobble: 1.1 },
      /* the second hop is deliberately not tiny: a 23px rise spent about six
         frames off the ground, which is a flicker rather than a bounce. Taller
         and slower, it reads as the parcel settling. */
      { rise: 0.058, dur: 0.27, squashY: 0.945, squashX: 1.04, wobble: 0.35 }
    ];

    entrance.fromTo(faller,
      { y: canFall ? dropFrom : 0, scaleY: 1.06, scaleX: 0.95, rotation: 0,
        transformOrigin: '50% 92%' },
      { y: 0, scaleY: 1, scaleX: 1, duration: fallDur, ease: 'power2.in' });

    var hopAt = fallDur;
    HOPS.forEach(function (hop, i) {
      var up = -stageRect.height * hop.rise;
      /* impact: squash on the way in */
      entrance.to(faller, {
        scaleY: hop.squashY, scaleX: hop.squashX, rotation: hop.wobble,
        duration: hop.dur * 0.3, ease: 'power2.out'
      }, hopAt);
      /* rise: stretch as it leaves the ground, then round over the apex */
      entrance.to(faller, {
        y: up, scaleY: 1 + (1 - hop.squashY) * 0.5, scaleX: 1 - (hop.squashX - 1) * 0.5,
        duration: hop.dur * 0.34, ease: 'power2.out'
      }, hopAt + hop.dur * 0.3);
      /* fall back down to the ground for the next impact. The rotation returns
         to ZERO rather than swinging negative: counter-rotating on every hop is
         what made a settling parcel look like it was being shaken. */
      entrance.to(faller, {
        y: 0, scaleY: 1, scaleX: 1, rotation: 0,
        duration: hop.dur * 0.36, ease: 'power2.in'
      }, hopAt + hop.dur * 0.64);
      hop.at = hopAt;
      hopAt += hop.dur;
    });
    /* final settle: everything back to true */
    entrance.to(faller, {
      scaleY: 1, scaleX: 1, rotation: 0, y: 0, duration: 0.16, ease: 'power2.out'
    }, hopAt);

    if (ground) {
      /* the shadow tracks every impact: wide and faint when the box is up,
         tight and dark at each contact */
      gsap.set(ground, { scaleX: 0.35, scaleY: 0.5, opacity: 0 });
      HOPS.forEach(function (hop, i) {
        var spread = 1.18 - i * 0.06;
        entrance.to(ground, {
          scaleX: spread, scaleY: 0.72 + i * 0.09, opacity: 1,
          duration: hop.dur * 0.3, ease: 'power2.out'
        }, hop.at);
        entrance.to(ground, {
          scaleX: 1 - (spread - 1) * 0.55, scaleY: 1.06, opacity: 0.72,
          duration: hop.dur * 0.34, ease: 'power2.out'
        }, hop.at + hop.dur * 0.3);
      });
      entrance.to(ground, { scaleX: 1, scaleY: 1, opacity: 1, duration: 0.2, ease: 'power2.out' }, hopAt);
    }


    /* ---- the scrub waits for what it actually depends on ----------------- */
    /* The keep-out solver and the flight paths are built from measured rects,
       and the copy column above the scene changes height when Baloo/Nunito
       land, which shifted the whole scene ~80px after the solver had cached its
       geometry. The front wall's alpha drives the keep-out test, so it has to
       be decoded first or the test silently degrades to its bounding rect.
       Both races keep a hung fetch from ever stalling the hero. */
    var fontsReady =
      document.fonts && document.fonts.ready
        ? Promise.race([
            document.fonts.ready,
            new Promise(function (r) { setTimeout(r, 1500); })
          ])
        : Promise.resolve();

    var wallReady = Promise.resolve();
    var wallImg = openFront && openFront.querySelector('img');
    if (wallImg && wallImg.decode) {
      wallReady = Promise.race([
        wallImg.decode().catch(function () {}),
        new Promise(function (r) { setTimeout(r, 900); })
      ]);
    }

    Promise.all([fontsReady, wallReady]).then(buildScrub).catch(function () {
      /* Never leave the reader holding a sealed parcel that no amount of
         scrolling opens: fall back to the settled composite the markup ships. */
      gsap.set(liveToys, { autoAlpha: 1, clearProps: 'transform' });
      gsap.set(lid, { clearProps: 'transform' });
      gsap.set(section.querySelectorAll('[data-unbox-toy-shadow]'),
        { clearProps: 'transform,opacity' });
      startIdle();
    });

    /* ================= 2. SCRUB ========================================== */
    function buildScrub() {

    /* Measure with the parcel wrapper at IDENTITY. The entrance may still be
       mid-bounce, and every rect below (toy landings, the mouth, the lid's
       resting box) would otherwise be captured with that bounce baked in. No
       paint happens inside this synchronous block, so nothing flickers. */
    var dropTransform = drop ? drop.style.transform : '';
    var parcelTransform = parcelEl ? parcelEl.style.transform : '';
    if (drop) drop.style.transform = 'none';
    if (parcelEl) parcelEl.style.transform = 'none';

    /* ---- keep-out: no toy may rest on a claim or the WhatsApp float --
       The settled positions come from CSS, which cannot know how wide a
       translated badge renders at a given breakpoint. So measure the real
       rects and push any offending toy out along the axis of least movement
       until it clears, then clamp it back inside the stage. The resulting
       offset becomes that toy's settled x/y, and the burst flies to it. */
    var KEEP_PAD = 14;
    /* How far (as a fraction of stage height) the cutout's keep-out zone is
       swept upward. 0 = static, the current design. If the cutout ever rides
       a parallax plane again this must equal that tween's travel fraction,
       and the tween must be re-added to the scrub: the polaroid's bug class
       was these two numbers drifting apart. */
    var CUTOUT_RIDE = 0;
    /* Toys must also clear EACH OTHER. The badge pushes are what put squishcube
       on the plush's shoulder: the solver cleared a claim and dropped the toy
       straight onto its neighbour, because nothing in the test knew the
       neighbour was there. Each solved toy joins the obstacle set for the ones
       after it. Smaller pad than a badge: props may sit close, just not touch. */
    var TOY_PAD = mobile ? 8 : 12;
    var placed = [];

    /* The box's front wall is a chevron: its bounding rect includes a large
       empty notch right above the mouth, which is exactly where a just-erupted
       toy should be able to sit. So sample the sprite's ALPHA once into a
       coarse grid and test against that instead of the rect. Same-origin theme
       asset, so the canvas is not tainted. */
    var wallGrid = null;
    (function buildWallGrid() {
      if (!openFront) return;
      var img = openFront.querySelector('img');
      if (!img || !img.complete || !img.naturalWidth) return;
      try {
        var N = 48;
        var c = document.createElement('canvas');
        c.width = c.height = N;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, N, N);
        var data = ctx.getImageData(0, 0, N, N).data;
        var g = new Uint8Array(N * N);
        for (var i = 0; i < N * N; i++) g[i] = data[i * 4 + 3] > 40 ? 1 : 0;
        wallGrid = { n: N, g: g };
      } catch (e) {
        wallGrid = null;      // fall back to the rect
      }
    })();

    function hitsWall(box) {
      if (!openFront) return false;
      var w = openFront.getBoundingClientRect();
      if (!w.width || box.right < w.left || box.left > w.right ||
          box.bottom < w.top || box.top > w.bottom) return false;
      if (!wallGrid) return true;                 // no grid: rect is the answer
      var n = wallGrid.n;
      var x0 = Math.max(0, Math.floor(((box.left - KEEP_PAD - w.left) / w.width) * n));
      var x1 = Math.min(n - 1, Math.ceil(((box.right + KEEP_PAD - w.left) / w.width) * n));
      var y0 = Math.max(0, Math.floor(((box.top - KEEP_PAD - w.top) / w.height) * n));
      var y1 = Math.min(n - 1, Math.ceil(((box.bottom + KEEP_PAD - w.top) / w.height) * n));
      for (var yy = y0; yy <= y1; yy++) {
        for (var xx = x0; xx <= x1; xx++) {
          if (wallGrid.g[yy * n + xx]) return true;
        }
      }
      return false;
    }
    /* the harness asserts with the same test, so check and solver cannot drift */
    window.__unboxHitsWall = hitsWall;

    function rects() {
      var list = [];
      /* THE CHILD'S FACE. Everything else in this list is a live DOM rect; she
         is part of the photograph, so her zone is a measured constant mapped
         through the same cover transform that places the box. Toys may fly
         over the picture freely, which is the intended effect, but one landing
         on her face reads as a rendering fault rather than as a burst. */
      /* the same photograph at every width now, so the same zones */
      [FACE_KEEPOUT, LEG_KEEPOUT].concat(mobile ? [HUG_KEEPOUT_PHONE] : []).forEach(function (k) {
        var z = photoRect(k);
        if (z) list.push(z);
      });
      section.querySelectorAll('[data-unbox-keepout]').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.width && r.height) list.push(r);
      });
      /* The girl cutout is STATIC (see td-hero.liquid: her source photo is
         frame-cropped at the shins, so any upward ride surfaces the raw cut
         edge), so CUTOUT_RIDE is 0 and her zone is just her live rect. The
         swept-union shape is kept on purpose: if she ever rides a plane
         again, set CUTOUT_RIDE to that tween's travel fraction AND add the
         matching tween in the scrub, the lockstep rule the polaroid obeyed
         (its two 0.25s lived here and in the scrub). Her size is fixed in
         CSS via aspect-ratio, which keeps the rect non-zero before the
         image decodes. */
      section.querySelectorAll('[data-unbox-cutout]').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var sweep = mobile ? 0 : stageRect.height * CUTOUT_RIDE;
        list.push({
          left: r.left, right: r.right,
          top: r.top - sweep, bottom: r.bottom,
          width: r.width, height: r.height + sweep
        });
      });
      /* THE HEADER. Not a [data-unbox-keepout] because it is not in this
         section, and it did not need to be in this list while the burst was
         clamped to the right of the viewport. The arc throws up and to the
         left over the photograph now, and its top reaches the nav row. */
      var hdr = document.querySelector('.td-header');
      if (hdr) {
        var hrr = hdr.getBoundingClientRect();
        if (hrr.width && hrr.height) list.push(hrr);
      }
      var waFloat = document.querySelector('.wa-float');
      if (waFloat) {
        var wr = waFloat.getBoundingClientRect();
        if (wr.width && wr.height) list.push(wr);
      }
      /* PHONES: the copy is split around the scene (headline on the wall
         above, the button below), and its wrappers are display: contents
         there, so they have no box of their own. Every line of it is a zone,
         the headline included, which has no keep-out hook of its own. */
      if (mobile && copy) {
        copy.querySelectorAll(':scope > *').forEach(function (el) {
          var cr = el.getBoundingClientRect();
          if (cr.width && cr.height) list.push(cr);
        });
      }
      return list;
    }

    function overlaps(a, b, pad) {
      if (pad === undefined) pad = KEEP_PAD;
      return (
        a.left < b.right + pad &&
        a.right > b.left - pad &&
        a.top < b.bottom + pad &&
        a.bottom > b.top - pad
      );
    }

    /* The whole solve is a FUNCTION so the cutout degrade ladder can re-run
       it: everything here happens synchronously before the first paint and
       before any tween exists, so a bounded number of re-solves is safe. A
       class change on the cutout followed by getBoundingClientRect forces a
       synchronous reflow, so each re-run measures the new geometry. */
    var zones = [];
    var lidBox = { width: 0, height: 0, left: 0, right: 0, top: 0, bottom: 0 };
    function solve() {
      placed.length = 0;
      zones = rects();
      /* re-measured: the fonts have landed since the entrance was built, and
         the photograph has decoded, so the anchor can finally be trusted */
      placeStage();
      stageRect = stage.getBoundingClientRect();
      var pinRect = pin.getBoundingClientRect();

      /* Where the lid ends up is a keep-out zone too. It is a large sprite and it
         settles into exactly the upper-right region the arc wants, so without
         this a toy lands on top of it. Measured by moving the lid to its resting
         pose, reading the box, and putting it back, all before any paint. */
      gsap.set(lid, { x: lidRest.x, y: lidRest.y, rotation: lidRest.rot });
      var lidImg = lid.querySelector('.unbox-lid-img');
      lidBox = (lidImg || lid).getBoundingClientRect();
      gsap.set(lid, { x: 0, y: 0, rotation: 0 });
      if (lidBox.width && lidBox.height) {
        /* The lid rests ROTATED, so its axis-aligned bounding box is much larger
           than the lid: on a phone the raw rect reserved the entire band above
           the box, which is exactly where the settled arc lives, and every toy
           got shoved below the parcel. Reserve the core instead. Toys are z 4
           and the lid z 3, so a clipped corner overlap costs nothing anyway. */
        var ix = lidBox.width * 0.16;
        var iy = lidBox.height * 0.16;
        zones.push({
          left: lidBox.left + ix, right: lidBox.right - ix,
          top: lidBox.top + iy, bottom: lidBox.bottom - iy,
          width: lidBox.width - ix * 2, height: lidBox.height - iy * 2
        });
      }

      /* The frame a toy may not leave.

         Desktop: the PIN, not the stage. Several toys are deliberately placed
         outside the stage box (left: -8%, top: -14%), and clamping to the stage
         shoved them back down onto the badge the push had just cleared. The pin
         spans the viewport, so this still guarantees a toy cannot create
         horizontal scroll.

         Mobile: the copy column is ABOVE the scene rather than beside it, so the
         same generous pin frame let a toy climb into the headline and the
         subcopy. Cap the rise just above the stage instead; the toys then ring
         the box, which is the composition that reads on a phone anyway. */
      /* on a phone the parcel stands mid-wall, so the burst may use the whole
         hero; toys may pass over the copy (the merchant's call), and the copy
         keep-outs still steer where they settle */
      var band = pinRect;
      var frame = mobile
        ? {
            left: pinRect.left,
            right: pinRect.right,
            top: band.top,
            bottom: band.bottom
          }
        : {
            left: pinRect.left,
            right: pinRect.right,
            top: pinRect.top - 0,
            bottom: pinRect.bottom
          };

      return liveToys.map(function (t) {
        /* The depth tier scales the PICTURE inside the toy, so the element's own
           rect is not what the reader sees: a `near` toy paints 6% larger than
           its box and a `far` one 8% smaller. Solve against the drawn size, or
           the near toys reserve too little room and end up touching. */
        var raw = t.getBoundingClientRect();
        var ds = t.getAttribute('data-depth') === 'near' ? 1.06 : 0.92;
        var gx = (raw.width * (ds - 1)) / 2;
        var gy = (raw.height * (ds - 1)) / 2;
        var r = {
          left: raw.left - gx, right: raw.right + gx,
          top: raw.top - gy, bottom: raw.bottom + gy,
          width: raw.width * ds, height: raw.height * ds
        };
        var dx = 0;
        var dy = 0;
        var mode = 'nominal';
        for (var pass = 0; pass < 6; pass++) {
          var moved = false;
          for (var i = 0; i < zones.length; i++) {
            var z = zones[i];
            var cur = {
              left: r.left + dx, right: r.right + dx,
              top: r.top + dy, bottom: r.bottom + dy
            };
            if (!overlaps(cur, z)) continue;
            /* smallest push that separates the two boxes */
            var pushDown = z.bottom + KEEP_PAD - cur.top;
            var pushUp = cur.bottom - (z.top - KEEP_PAD);
            var pushRight = z.right + KEEP_PAD - cur.left;
            var pushLeft = cur.right - (z.left - KEEP_PAD);
            var best = Math.min(pushDown, pushUp, pushRight, pushLeft);
            if (best === pushDown) dy += pushDown;
            else if (best === pushUp) dy -= pushUp;
            else if (best === pushRight) dx += pushRight;
            else dx -= pushLeft;
            moved = true;
          }
          /* already-solved toys are obstacles too, on the same smallest-push rule */
          for (var p = 0; p < placed.length; p++) {
            var pz = placed[p];
            var pc = {
              left: r.left + dx, right: r.right + dx,
              top: r.top + dy, bottom: r.bottom + dy
            };
            if (!overlaps(pc, pz, TOY_PAD)) continue;
            var pDown = pz.bottom + TOY_PAD - pc.top;
            var pUp = pc.bottom - (pz.top - TOY_PAD);
            var pRight = pz.right + TOY_PAD - pc.left;
            var pLeft = pc.right - (pz.left - TOY_PAD);
            var pBest = Math.min(pDown, pUp, pRight, pLeft);
            if (pBest === pDown) dy += pDown;
            else if (pBest === pUp) dy -= pUp;
            else if (pBest === pRight) dx += pRight;
            else dx -= pLeft;
            moved = true;
          }
          /* the front wall counts too: push straight up out of the box */
          var cur2 = { left: r.left + dx, right: r.right + dx,
                       top: r.top + dy, bottom: r.bottom + dy };
          if (hitsWall(cur2)) {
            var wr2 = openFront.getBoundingClientRect();
            dy -= cur2.bottom - (wr2.top - KEEP_PAD);
            moved = true;
          }
          if (!moved) break;
        }

        /* Clamp to the PIN frame BEFORE the final check, not after. Several toys
           are deliberately placed outside the stage box (left: -8%, top: -14%),
           and clamping to the stage shoved them back down onto the badge the push
           had just cleared, so the pin is the right frame. It has to come first:
           clamping a solved offset afterwards can slide a toy straight back into
           the neighbour it had just been separated from, which is how plush and
           dumpling-teal ended up 2px apart at 360. */
        function clampToPin(ox, oy) {
          var minX = frame.left + 4 - r.left;
          var maxX = frame.right - 4 - r.right;
          var minY = frame.top + 4 - r.top;
          var maxY = frame.bottom - 4 - r.bottom;
          return [
            Math.max(Math.min(minX, 0), Math.min(Math.max(maxX, 0), ox)),
            Math.max(Math.min(minY, 0), Math.min(Math.max(maxY, 0), oy))
          ];
        }
        var clamped = clampToPin(dx, dy);
        dx = clamped[0];
        dy = clamped[1];

        /* The greedy push resolves one zone at a time, so it can oscillate
           between two zones closer together than the toy is tall (the countdown
           chip and the Ships badge, with a 68px gap, against a 62px toy). When
           that happens, search outward for the nearest offset that clears
           EVERYTHING at once. Deterministic, and it always finds a spot if the
           viewport has one. */
        /* `partial` relaxes ONLY the box wall: the toy's top 55% must still stand
           clear of it, so it reads as resting behind the rim rather than
           vanishing (toys are z 4, the front wall z 5, so "on the wall" means
           hidden, not ugly). Claims and neighbours are never relaxed. */
        function clearAt(ox, oy, partial) {
          var box = {
            left: r.left + ox, right: r.right + ox,
            top: r.top + oy, bottom: r.bottom + oy
          };
          for (var z = 0; z < zones.length; z++) {
            if (overlaps(box, zones[z])) return false;
          }
          for (var q = 0; q < placed.length; q++) {
            if (overlaps(box, placed[q], TOY_PAD)) return false;
          }
          if (!partial) return !hitsWall(box);
          return !hitsWall({
            left: box.left, right: box.right, top: box.top,
            bottom: box.top + (box.bottom - box.top) * 0.55
          });
        }

        /* Finer and wider than the greedy pass: with a badge owning one corner
           and the box wall the lower half, the nearest clear spot can be a long
           way off and off a coarse lattice. Rings are 10px apart with 24
           directions, and every candidate is already inside the pin, so nothing
           found here can be clamped afterwards back onto a neighbour. */
        function ringSearch(partial) {
          var step = 10;
          for (var ring = 1; ring <= 40; ring++) {
            for (var a = 0; a < 24; a++) {
              var ang = (a / 24) * Math.PI * 2 - Math.PI / 2;   // bias upward
              var cx = Math.round(Math.cos(ang) * ring * step);
              var cy = Math.round(Math.sin(ang) * ring * step);
              if (r.left + cx < frame.left + 4 || r.right + cx > frame.right - 4) continue;
              if (r.top + cy < frame.top) continue;
              if (r.bottom + cy > frame.bottom - 4) continue;
              if (!clearAt(cx, cy, partial)) continue;
              return [cx, cy];
            }
          }
          return null;
        }

        if (!clearAt(dx, dy)) {
          /* At 360 with five toys, both badges, the CTA, the countdown chip, the
             trust row and the WhatsApp float in play, there is sometimes no fully
             clear spot left. Degrade in a fixed order rather than at random:
             anywhere clear, else behind the box rim, and only then a blind shove.
             A toy may never end up on a claim or the CTA. */
          var hit = ringSearch(false);
          mode = hit ? 'ring' : 'ring-partial';
          if (!hit) hit = ringSearch(true);
          if (hit) {
            dx = hit[0];
            dy = hit[1];
          } else {
            mode = 'shove';
            dy -= r.height;
            var re = clampToPin(dx, dy);
            dx = re[0];
            dy = re[1];
          }
        }

        placed.push({
          left: r.left + dx, right: r.right + dx,
          top: r.top + dy, bottom: r.bottom + dy
        });

        return {
          el: t,
          shadow: t.querySelector('[data-unbox-toy-shadow]'),
          inner: t.querySelector('.unbox-toy-in'),
          near: t.getAttribute('data-depth') === 'near',
          x: dx,
          y: dy,
          mode: mode,
          /* Does this toy still land on the lid? The lid is z 6, above the toys'
             z 4, so such a toy is drawn UNDER the lid and reads as tucked behind
             it. The solver already pushes toys off the lid's CORE, but one that
             settles in the outer band still overlaps. Rather than shove it further
             and break the arc, lift it above the lid once it is out of the box. */
          overLid: (function () {
            var fr = { left: r.left + dx, right: r.right + dx,
                       top: r.top + dy, bottom: r.bottom + dy };
            return !!(lidBox.width && fr.left < lidBox.right && fr.right > lidBox.left &&
                      fr.top < lidBox.bottom && fr.bottom > lidBox.top);
          })(),
          rot: Number(gsap.getProperty(t, 'rotation')) || 0,
          rect: r
        };
      });
    }

    /* ---- cutout degrade ladder ----------------------------------------- */
    /* If the toy solver has to resort to a blind shove while the cutout is
       in play, the decoration yields, never the burst. Tier 1 shrinks her
       (16% to 13% of the stage), tier 2 removes her. 'shove' ONLY:
       'ring-partial' (tucked behind the rim) is a pre-existing, accepted
       tier that happens without her, and stepping the ladder on it would
       delete her for no visual gain. Bounded at three solves, all before
       first paint. The harness reads window.__unboxDebug.cutout. */
    var cutoutEl = section.querySelector('[data-unbox-cutout]');
    var cutoutState =
      cutoutEl && getComputedStyle(cutoutEl).display !== 'none' ? 'full' : 'absent';
    function anyShove(list) {
      for (var si = 0; si < list.length; si++) {
        if (list[si].mode === 'shove') return true;
      }
      return false;
    }
    var settle = solve();
    if (cutoutState === 'full' && anyShove(settle)) {
      cutoutEl.classList.add('unbox-cutout--small');
      cutoutState = 'shrunk';
      settle = solve();
    }
    if (cutoutState === 'shrunk' && anyShove(settle)) {
      cutoutEl.classList.remove('unbox-cutout--small');
      cutoutEl.classList.add('unbox-cutout--off');
      cutoutState = 'removed';
      settle = solve();
    }

    /* ---- ONE launch origin for every burst element ----------------------- */
    /* The mouth centre, INSIDE the box and well below the front rim, so a piece
       at its starting scale is entirely behind the front wall and has to climb
       out through the rim to be seen. Toys and confetti share this exactly:
       giving confetti its own spawn is what had it appearing at the rim and
       over the box face, and giving the two smallest toys a landing-relative
       origin is what made them slide in from behind the box instead of rising
       out of it. Anything that launches, launches from here. */
    /* The spawn depth is DERIVED from the front wall's alpha, not guessed as a
       percentage of the stage. A fixed 62% happened to sit behind cardboard at
       the old box size and landed in the open interior at the new one, so every
       toy and every confetti piece was visible before it launched. Find the rim
       (the first opaque row down the wall's centre column) and drop well below
       it: then "hidden at spawn" is a property of the geometry rather than a
       number that silently stops being true when the art changes. */
    var mouth = (function () {
      var w = openFront.getBoundingClientRect();
      var cx = w.left + w.width * 0.5;
      if (!wallGrid || !w.height) {
        return { x: cx, y: w.top + w.height * 0.62 };
      }
      var n = wallGrid.n;
      var col = Math.floor(n * 0.5);
      var rim = -1;
      for (var yy = 0; yy < n; yy++) {
        if (wallGrid.g[yy * n + col]) { rim = yy; break; }
      }
      if (rim < 0) return { x: cx, y: w.top + w.height * 0.62 };
      /* 24% of the wall's height below the rim clears the tallest spawning
         piece (a toy at 0.34 scale) by a comfortable margin. Sealing the wall's
         holes raised the grid's first-opaque row slightly, which at 18% left a
         couple of toy pixels grazing the antialiased rim edge. */
      var deep = Math.min(n - 1, rim + Math.round(n * 0.24));
      return { x: cx, y: w.top + ((deep + 0.5) / n) * w.height };
    })();
    var mouthX = mouth.x;
    var mouthY = mouth.y;

    function launchOffset(rect) {
      return {
        dx: mouthX - (rect.left + rect.width / 2),
        dy: mouthY - (rect.top + rect.height / 2)
      };
    }

    settle.forEach(function (s) {
      /* the mouth, not the mouth-plus-its-own-landing-offset: they all erupt
         from the same point inside the box */
      var o = launchOffset(s.rect);
      s.dx = o.dx;
      s.dy = o.dy;
    });

    /* every confetti piece, measured individually: they are absolutely
       positioned at one host point but the shapes differ in size */
    var bitLaunch = confetti.map(function (b) {
      return launchOffset(b.getBoundingClientRect());
    });

    /* diagnostics for the geometry harness: what the keep-out solver decided */
    window.__unboxDebug = {
      fonts: document.fonts ? document.fonts.status : 'n/a',
      /* which rung of the cutout degrade ladder shipped this load */
      cutout: cutoutState,
      stage: {
        l: Math.round(stageRect.left),
        t: Math.round(stageRect.top),
        w: Math.round(stageRect.width)
      },
      zones: zones.map(function (z) {
        return { l: Math.round(z.left), t: Math.round(z.top), r: Math.round(z.right), b: Math.round(z.bottom) };
      }),
      settle: settle.map(function (s) {
        return {
          name: (s.el.className.match(/unbox-toy--([a-z-]+)/) || [])[1],
          mode: s.mode,
          x: Math.round(s.x), y: Math.round(s.y),
          l: Math.round(s.rect.left), t: Math.round(s.rect.top)
        };
      })
    };

    /* ---- park everything at the launch origin ---------------------------- */
    /* Toys and confetti alike: at the mouth, inside the box, small. They stay
       fully OPAQUE from here on; being invisible at spawn is the front wall's
       job, not a fade's. That is what makes them read as physically inside the
       parcel rather than as sprites being switched on. */
    var SPAWN_SCALE = 0.34;

    settle.forEach(function (s) {
      gsap.set(s.el, {
        x: s.dx,
        y: s.dy,
        rotation: s.rot > 0 ? -26 : 26,
        scale: SPAWN_SCALE,
        autoAlpha: 1
      });
      if (s.shadow) gsap.set(s.shadow, { scale: 0.2, opacity: 0 });
    });

    confetti.forEach(function (b, i) {
      gsap.set(b, {
        x: bitLaunch[i].dx,
        y: bitLaunch[i].dy,
        scale: SPAWN_SCALE,
        rotation: 0,
        autoAlpha: 1
      });
    });

    /* The lid stays at identity (seated) and opaque. Its flight is the only
       thing that opens the box: there is no state to swap and nothing to fade,
       so the first frame of the rise is the same pixels the parcel was made
       of. */
    gsap.set(lid, { x: 0, y: 0, rotation: 0, autoAlpha: 1 });

    var lidApexX = lidRest.x * 0.66;
    var lidApexRot = lidRest.rot * 0.75;

    /* measuring is done: hand the wrapper back to whatever the entrance was
       doing with it */
    if (drop) drop.style.transform = dropTransform;
    if (parcelEl) parcelEl.style.transform = parcelTransform;

    var d = mobile ? 0.5 : 1;

    /* Promote the moving sprites now, not on pin activation. The hero is the
       first thing on screen so the layers are needed within a moment anyway,
       and creating ~30 of them at the instant the pin engages cost a visible
       stall mid-scrub. They are dropped again on leave. */
    section.classList.add('unbox-scrubbing');

    var hdrEl = document.querySelector('.td-header');
    var hdrH = hdrEl ? hdrEl.getBoundingClientRect().height : 0;
    var scrubVars = {
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: pin,
        /* phones pin just under the sticky nav, so the whole band shows */
        start: mobile ? 'top top+=' + Math.round(hdrH) : 'top top',
        end: '+=' + pinVh + '%',
        pin: true,
        /* 0.8 rather than 1: still smooths the wheel's stepped deltas into
           continuous motion, but the scene tracks the finger closely enough
           that it never feels like it is lagging behind the scroll. */
        scrub: 0.8,
        anticipatePin: 1,
        onEnterBack: function () {
          section.classList.remove('unbox-idle');
          section.classList.add('unbox-scrubbing');
        },
        onUpdate: function (self) {
          /* the closed box's waiting-breath is a CSS `translate` loop; leaving
             it running through the scrub reads as the base drifting during the
             swap, so it is dropped the moment the reader engages */
          if (self.progress > 0.004) section.classList.remove('unbox-landed');
          else if (landed) section.classList.add('unbox-landed');
        },
        onLeave: function () {
          /* done with the sequence: drop the promoted layers and settle */
          section.classList.remove('unbox-scrubbing');
          startIdle();
        }
      }
    };
    var tl = gsap.timeline(scrubVars);

    /* 0-0.20: the sealed parcel breathes; the highlight wipes in. The whole
       parcel moves, so box and lid stay locked together. */
    /* the INNER wrapper: the entrance owns `drop`, and a tween that shares an
       element with it records a mid-fall start value as progress 0 */
    var parcel = parcelEl || open;
    tl.to(parcel, { y: -10 * d, ease: 'sine.inOut', duration: 0.1 }, 0);
    tl.to(parcel, { y: 0, ease: 'sine.inOut', duration: 0.1 }, 0.1);
    if (hl) {
      tl.call(function () {
        hl.classList.add('hl-on');
      }, null, 0.03);
    }

    /* left column: readable throughout, these only move between visible states */
    if (sub) tl.from(sub, { y: 12 * d, duration: 0.18, ease: 'power1.out' }, 0.06);
    if (chip) tl.from(chip, { y: 12 * d, duration: 0.18, ease: 'power1.out' }, 0.14);

    /* 0.20-0.28: anticipation squash on the whole parcel, planted on its bottom
       edge, rebounding to EXACTLY scale 1 by 0.28. 0.28-0.30 is dead still, so
       the lid lifts off an untransformed body. */
    tl.to(parcel, {
      scaleY: 0.9,
      scaleX: 1.06,
      transformOrigin: '50% 92%',
      duration: 0.05,
      ease: 'power2.in'
    }, 0.2);
    tl.to(parcel, { scaleY: 1.03, scaleX: 0.98, duration: 0.02, ease: 'power2.out' }, 0.25);
    tl.to(parcel, { scaleY: 1, scaleX: 1, duration: 0.01, ease: 'power1.out' }, 0.27);

    /* 0.30-0.60: THE LID LEAVES, and that alone is what opens the box. Nothing
       swaps, nothing fades, nothing is switched on. It is the same sprite that
       was seated a frame earlier, so there is no jump to hide:

         straight up off the rim, tipping backward as it breaks the seal
         up and RIGHT to an apex where it is fully above the box
         then it falls in BEHIND the top-right corner and stays there

       The apex is where its depth changes: z 3 (over the interior, which it
       had to cover while seated) becomes z 1 (behind the box, where it rests).
       With the lid entirely above the box top at that frame, the flip has
       nothing visible to change, so the toss reads as one continuous motion
       that ends tucked behind the corner instead of pasted over the rim. */
    tl.to(lid, {
      y: lidRest.y * 0.34,
      rotation: lidRest.rot * 0.3 - 4,
      duration: 0.045,
      ease: 'power2.out'
    }, 0.3);
    tl.to(lid, {
      x: lidApexX,
      y: lidRest.y * 1.18,
      rotation: lidApexRot,
      duration: 0.075,
      ease: 'power1.out'
    }, 0.345);
    tl.to(lid, {
      x: lidRest.x,
      y: lidRest.y,
      rotation: lidRest.rot,
      duration: 0.14,
      ease: 'power1.in'
    }, 0.43);

    /* Box recoil as the seal breaks: 1 -> 1.04 -> 1 on both walls together. */
    var boxHalves = [open, openFront];
    tl.to(boxHalves, { scale: 1.04, transformOrigin: '50% 88%', duration: 0.035, ease: 'power2.out' }, 0.3);
    tl.to(boxHalves, { scale: 1, duration: 0.06, ease: 'power2.inOut' }, 0.335);

    /* Mouth flash: a ~200ms bloom behind the toys as they erupt */

    /* CTA pulse, timed to the pop: the moment of delight cues the click */
    if (cta) {
      tl.to(cta, { scale: 1.04, duration: 0.12, ease: 'sine.inOut' }, 0.3);
      tl.to(cta, { scale: 1, duration: 0.13, ease: 'sine.inOut' }, 0.42);
    }

    /* ---- THE BURST: one physics path, every element on it ---------------- */
    /* Shared by toys and confetti alike, because the inconsistency between them
       was the most visible flaw: two toys rose out of the box while the other
       two slid in from behind it, and the confetti appeared at the rim.

         y  leads on back.out, so the piece shoots up hard, overshoots its apex
            and drops back into place
         x  lags by ~a fifth of the flight, so the path is a steep climb that
            only then arcs outward. Nothing can read as sliding in sideways,
            because for the first part of every flight x has not moved at all
         scale runs SPAWN_SCALE -> full through the rim, so it grows out of depth

       Every launch is staggered 40-80ms in timeline terms so the eruption is a
       sequence rather than one pop. */
    function launch(el, at, dur, from, to, spin, rise) {
      /* y is a genuine ARC over an apex, not a single eased move to the target.
         A lone back.out tween only overshoots its destination, so any piece
         whose resting place sits BELOW the mouth (butter on the left, popit on
         a phone) travelled straight down and never rose: it read as sliding out
         from behind the box rather than erupting from inside it. The apex is
         above both ends by construction, so every piece climbs first. */
      var apex = Math.min(from.y, to.y) - rise;
      tl.to(el, { y: apex, duration: dur * 0.42, ease: 'power2.out' }, at);
      tl.to(el, { y: to.y + 8, duration: dur * 0.44, ease: 'power1.in' }, at + dur * 0.42);
      /* soft settle with a slight overshoot, so it lands rather than stops */
      tl.to(el, { y: to.y, duration: dur * 0.14, ease: 'power2.out' }, at + dur * 0.86);
      /* x does not begin until the APEX, so every piece is clear of the box top
         before it travels sideways at all. Starting at a fifth of the flight
         meant the lateral move began while the piece was still level with the
         cardboard, and it passed through the wall on its way out. */
      tl.to(el, { x: to.x, duration: dur * 0.58, ease: 'power2.out' }, at + dur * 0.42);
      tl.to(el, { rotation: spin, duration: dur, ease: 'power1.out' }, at);
      tl.to(el, { scale: to.scale * 1.06, duration: dur * 0.6, ease: 'power2.out' }, at);
      tl.to(el, { scale: to.scale, duration: dur * 0.4, ease: 'power1.inOut' }, at + dur * 0.6);
    }

    /* The apex has to clear the BOX TOP, not just be "some way up".
       At a fixed fraction of the stage the apex still sat ~120px below the box
       top, so a toy heading for a left- or right-hand resting place began its
       sideways travel while still level with the cardboard and cut straight
       through the wall (the butter, most visibly). Measured, so it holds at
       every width. */
    var boxTopY = open.getBoundingClientRect().top;
    var TOY_RISE = Math.max(stageRect.height * 0.26, (mouthY - boxTopY) + 34);

    /* NOTHING leaves the box until the lid is off the mouth. The burst used to
       start at 0.30, the same instant the lid began to lift, so confetti was
       streaming out of a box that still looked shut. The lid clears the rim at
       ~0.42 (lift 0.30-0.345, then up to its apex by 0.42), so that is the
       earliest anything may appear. */
    var BURST_AT = 0.42;

    /* THE FURTHEST PIECE LEAVES FIRST, and the stagger is half what it was.
       The old order was the DOM's, so `butter` launched last (index 6, +0.132)
       and also had the longest way to travel, ending its flight at 0.832 of
       the scrub while `plush` was done at 0.70. It read exactly as it was: one
       toy still sliding into place long after the burst had finished.

       Nothing in a real burst waits its turn. Everything leaves within a few
       frames and the far pieces simply take longer to arrive, so the launch
       order is now by distance, furthest first, and the duration grows with
       distance. The whole field now lands inside about one hundredth of the
       scrub instead of thirteen. */
    var travel = settle.map(function (it, i) {
      var dx = it.x - it.dx;
      var dy = it.y - it.dy;
      return { i: i, d: Math.sqrt(dx * dx + dy * dy) };
    });
    var maxTravel = Math.max.apply(null, travel.map(function (t) { return t.d; })) || 1;
    var rank = [];
    travel.slice().sort(function (a, b) { return b.d - a.d; })
      .forEach(function (t, k) { rank[t.i] = k; });

    settle.forEach(function (item, i) {
      var far = travel[i].d / maxTravel;
      var at = BURST_AT + rank[i] * 0.012;   // ~22ms apart at a typical pin
      var dur = 0.26 + far * 0.06;
      launch(item.el, at, dur,
        { x: item.dx, y: item.dy },
        { x: item.x, y: item.y, scale: 1 },
        item.rot, TOY_RISE * (0.84 + far * 0.30));
      if (item.inner) {
        tl.fromTo(item.inner,
          { rotation: item.near ? -18 : 14 },
          { rotation: 0, duration: dur, ease: 'power1.out' }, at);
      }
      if (item.shadow) {
        tl.fromTo(item.shadow, { scale: 0.2, opacity: 0 },
          { scale: 1, opacity: 1, duration: dur, ease: 'power2.out' }, at + dur * 0.2);
      }
      /* Lift over the lid only at the END of the flight. Doing it at spawn would
         put the toy above the front wall too, and it would show through the
         closed box; by `at + dur` it is out and settling. A zero-duration set,
         so scrubbing back up restores the spawn depth. */
      if (item.overLid) {
        tl.set(item.el, { zIndex: 7 }, at + dur);
      }
    });

    /* Confetti: same origin, same curve, same emergence. Spread wider than the
       toys and in every upward direction, so the pieces frame the arc instead
       of tracking it. */
    confetti.forEach(function (bit, i) {
      var spread = (mobile ? 150 : 260) + (i % 7) * (mobile ? 26 : 44);
      /* Fan across the UPPER half only: -175deg to about -5deg, plus a little
         jitter. The previous sweep was 279deg wide, so its tail pointed at
         +117deg, straight down: those pieces sank out of the box instead of
         erupting from it, which is exactly the inconsistency being fixed. */
      var ang = -Math.PI * 0.97 + (i / confetti.length) * Math.PI * 0.94 + (i % 3) * 0.1;
      var at = BURST_AT + (i % 8) * 0.010;
      var dur = 0.26 + (i % 4) * 0.018;
      launch(bit, at, dur,
        { x: bitLaunch[i].dx, y: bitLaunch[i].dy },
        {
          x: bitLaunch[i].dx + Math.cos(ang) * spread,
          y: bitLaunch[i].dy + Math.sin(ang) * spread * 0.86,
          scale: 1
        }, 120 + (i % 7) * 40, spread * 0.16);
      /* keeps drifting and falling after the burst rather than freezing */
      tl.to(bit, {
        x: '+=' + (Math.cos(ang) * 18).toFixed(1),
        y: '+=' + (30 + (i % 4) * 11),
        rotation: '+=' + (30 + (i % 5) * 20),
        autoAlpha: 0,
        duration: 0.26,
        ease: 'sine.in'
      }, 0.8);
    });

    /* 0.70-1.0: settle */
    if (copy && !mobile) {
      tl.to(copy, { y: -20, duration: 0.4, ease: 'sine.out' }, 0.6);
    }
    if (trust) {
      tl.to(trust, { autoAlpha: 1, y: 0, duration: 0.14, ease: 'power1.out' }, 0.78);
    }

    /* background planes across the whole pin */
    if (far) tl.to(far, { y: -0.1 * stageRect.height, scale: 1.04, duration: 1 }, 0);
    if (mid) tl.to(mid, { y: -0.25 * stageRect.height, rotation: 4, duration: 1 }, 0);
    /* The girl cutout does NOT ride any plane: her sprite is frame-cropped at
       the shins, and riding would lift that raw edge off the stage bottom
       (she would levitate). If a ride ever returns, add its tween here AND
       set CUTOUT_RIDE (rects()) to the same travel fraction, in lockstep. */
    if (wash) tl.to(wash, { opacity: 1, duration: 0.62 }, 0.24);

    /* Fonts and lazy images settle late and change the page height; one
       refresh after load squares the pin distance. */
    window.addEventListener('load', function () {
      window.ScrollTrigger.refresh();
    });
    } /* end buildScrub */
  }
})();
